import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ReactComponent as saveIkona } from "../Slike/save.svg";
import { ReactComponent as homeIkona } from "../Slike/homeIkona.svg";
import { useNavigate } from "react-router-dom";
import { FaHome, FaBookmark } from "react-icons/fa";

const DetailTicket = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [saved, setSaved] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/getIncident.php`,
        );

        const data = await res.json();

        if (!data?.incidents) {
          setTicket(false);
          return;
        }

        const found = data.incidents.find((t) => String(t.id) === String(id));

        setTicket(found || false);

        const ev = await fetch(
          `${process.env.REACT_APP_API_URL}/getIncidentEvent.php?incident_id=${id}`,
        );

        const evData = await ev.json();
        setEvents(evData.events || []);
      } catch (err) {
        console.log("LOAD ERROR:", err);
        setTicket(false);
      }
    };

    load();
  }, [id]);

  // =========================
  // CHECK SAVED ON LOAD
  // =========================
  useEffect(() => {
    const initSaved = async () => {
      if (!user?.id || !id) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/checkSavedIncident.php?user_id=${user.id}&incident_id=${id}`,
        );

        const data = await res.json();
        setSaved(!!data.saved);
      } catch (err) {
        console.log("CHECK SAVED ERROR:", err);
      }
    };

    initSaved();
  }, [id, user?.id]);
  // =========================
  // ROLE LOGIC
  // =========================
  const isAdmin = Number(user?.role_id) === 3;
  const isAssigned = !!ticket?.assigned_to;
  const isMine = String(ticket?.assigned_to) === String(user?.id);
  const canWork = isAdmin || isMine;
  const navigate = useNavigate();

  const isLocked = ticket?.status === "Closed";

  // =========================
  // EVENT
  // =========================
  const addEvent = async (type, message) => {
    if (isLocked) return;

    await fetch(`${process.env.REACT_APP_API_URL}/addIncidentEvent.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        incident_id: id,
        user_id: user.id,
        event_type: type,
        message,
      }),
    });

    const ev = await fetch(
      `${process.env.REACT_APP_API_URL}/getIncidentEvent.php?incident_id=${id}`,
    );
    const data = await ev.json();
    setEvents(data.events || []);
  };

  // =========================
  // NOTE
  // =========================
  const addNote = async () => {
    if (isLocked) return;
    if (!note.trim()) return;

    await addEvent("note", note);
    setNote("");
  };

  // =========================
  // TAKE INCIDENT
  // =========================
  const takeIncident = async () => {
    if (isLocked) return;

    await fetch(`${process.env.REACT_APP_API_URL}/assignIncident.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        incident_id: id,
        user_id: user.id,
      }),
    });

    setTicket((prev) => ({
      ...prev,
      assigned_to: user.id,
      status: "In Progress",
    }));

    addEvent("assignment", `Incident taken by ${user.name}`);
  };

  // =========================
  // FIX SAVE LOGIC (TOGGLE + LOAD STATE
  // =========================
  const toggleSaveIncident = async () => {
    if (!user?.id) return;

    if (saved) {
      await fetch(`${process.env.REACT_APP_API_URL}/unsaveIncident.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          incident_id: id,
          user_id: user.id,
        }),
      });

      setSaved(false);
    } else {
      await fetch(`${process.env.REACT_APP_API_URL}/saveIncident.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          incident_id: id,
          user_id: user.id,
        }),
      });

      setSaved(true);
    }
  };
  // =========================
  // AUTO LOAD
  // =========================
  const checkSaved = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/checkSavedIncident.php?user_id=${user.id}&incident_id=${id}`,
    );

    const data = await res.json();
    setSaved(data.saved);
  };
  // =========================
  // STATUS CHANGE
  // =========================
  const changeStatus = async (status) => {
    if (isLocked) return;
    if (!note.trim()) return;

    setLoading(true);

    await fetch(`${process.env.REACT_APP_API_URL}/updateStatus.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        incident_id: id,
        user_id: user.id,
        status,
        message: note,
      }),
    });

    setTicket((prev) => ({ ...prev, status }));
    await addEvent("status_change", `Status changed to ${status}: ${note}`);

    setNote("");
    setLoading(false);
  };

  // =========================
  // STATUS STYLE
  // =========================
  const statusBadge = (status) => {
    const map = {
      Open: "bg-emerald-100 text-emerald-700",
      "In Progress": "bg-blue-100 text-blue-700",
      Pending: "bg-yellow-100 text-yellow-800",
      Closed: "bg-red-100 text-red-700",
    };
    return map[status] || "bg-gray-100 text-gray-700";
  };

  if (ticket === null) return <div className="p-6">Loading...</div>;
  if (ticket === false) return <div className="p-6">Not found</div>;

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-6 space-y-6">
      {/* HEADER */}
      <div className="bg-white shadow rounded-2xl p-6 border-l-4 border-[#0f2d56]">
        <div className="flex justify-between items-start">
          {/* LEFT SIDE */}
          <div className="flex items-start gap-4">
            {/* ICON BUTTONS */}
            <div className="flex flex-col gap-2 mt-1">
              {/* HOME */}
              <button
                onClick={() => navigate("/tickets")}
                className="p-2 rounded-lg bg-[#0f2d56]/10 hover:bg-[#0f2d56]/20 transition"
                title="Back to tickets"
              >
                <FaHome className="w-5 h-5 text-[#0f2d56]" />
              </button>

              {/* SAVE / BOOKMARK */}
              <button
                onClick={toggleSaveIncident}
                className="p-2 rounded-lg bg-[#0f2d56]/10 hover:bg-[#0f2d56]/20 transition"
                title="Save incident"
              >
                <FaBookmark
                  className={`w-5 h-5 transition ${
                    saved ? "text-[#0f2d56]" : "text-gray-400"
                  }`}
                />
              </button>
            </div>

            {/* TITLE */}
            <div>
              <h1 className="text-2xl font-bold">INC-{ticket.id}</h1>
              <p className="text-gray-600 text-sm">{ticket.title}</p>
            </div>
          </div>

          {/* STATUS */}
          <span
            className={`px-4 py-1 rounded-full font-semibold ${statusBadge(ticket.status)}`}
          >
            {ticket.status}
          </span>
        </div>

        <p className="mt-3 text-gray-700">{ticket.description}</p>
      </div>

      {/* INCIDENT INFO CARD */}
      <div className="bg-white shadow rounded-2xl p-6 border-l-4 border-[#0f2d56]">
        <h3 className="font-bold text-[#0f2d56] mb-4">Incident Information</h3>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Priority</span>
            <span className="font-semibold">{ticket.priority}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Category</span>
            <span className="font-semibold">
              {ticket.category_name || "No category"}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Created by</span>
            <span className="font-semibold">
              {ticket.created_by_name || "-"}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Assigned to</span>
            <span className="font-semibold">
              {ticket.assigned_to_name || "Unassigned"}
            </span>
          </div>

          <div className="border-t pt-3 mt-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Created</span>
              <span className="text-xs text-gray-700">{ticket.created_at}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Updated</span>
              <span className="text-xs text-gray-700">{ticket.updated_at}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Closed</span>
              <span className="text-xs text-gray-700">
                {ticket.closed_at || "-"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* STATUS FLOW */}
      <div className="bg-white shadow rounded-2xl p-6">
        <h2 className="font-bold mb-4">Status Flow</h2>

        <div className="flex justify-between">
          {["Open", "In Progress", "Pending", "Closed"].map((s, i) => (
            <div key={i} className="flex flex-col items-center w-full">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  ticket.status === s
                    ? "bg-[#0f2d56] text-white"
                    : "bg-gray-200"
                }`}
              >
                {i + 1}
              </div>
              <p className="text-xs mt-1">{s}</p>
            </div>
          ))}
        </div>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="md:col-span-2 space-y-6">
          {/* WORK */}
          {canWork && (
            <div className="bg-white shadow rounded-2xl p-6 border-l-4 border-[#0f2d56]">
              <h2 className="font-bold text-[#0f2d56] mb-4">Work Log</h2>

              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isLocked}
                className="w-full border rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#0f2d56]/30 bg-white"
                placeholder="Write update..."
              />

              {/* ACTIONS */}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => changeStatus("In Progress")}
                  disabled={isLocked}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200"
                >
                  In Progress
                </button>

                <button
                  onClick={() => changeStatus("Pending")}
                  disabled={isLocked}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-yellow-50 text-yellow-800 border border-yellow-200"
                >
                  Pending
                </button>

                <button
                  onClick={() => changeStatus("Closed")}
                  disabled={isLocked}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200"
                >
                  Close Ticket
                </button>

                <button
                  onClick={addNote}
                  disabled={isLocked}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300"
                >
                  Add Note
                </button>
              </div>
            </div>
          )}

          {/* TIMELINE */}
          <div className="bg-white shadow rounded-2xl p-6">
            <h2 className="font-bold mb-4">Activity</h2>

            {events.map((e, i) => (
              <div key={i} className="border-l-4 border-[#0f2d56] pl-4 mb-4">
                <p className="text-xs text-gray-500 flex justify-between">
                  <span>{e.created_at}</span>
                  <span className="uppercase text-[10px] bg-gray-100 px-2 py-0.5 rounded">
                    {e.event_type}
                  </span>
                </p>

                <p className="text-sm text-gray-800 mt-1">{e.message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-2xl p-6 border-l-4 border-[#0f2d56]">
            {!isAssigned && !isLocked && (
              <button
                onClick={takeIncident}
                className="w-full bg-[#0f2d56] text-white py-2 rounded-xl"
              >
                Take Incident
              </button>
            )}

            <div className="mt-5 space-y-4 text-sm">
              <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
                <span>Assigned</span>
                <span>{ticket.assigned_to_name || "Unassigned"}</span>
              </div>

              <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
                <span>Created by</span>
                <span>{ticket.created_by_name || "-"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailTicket;
